import { dependencyTier, type DependencyGrade } from './dependencyCriticality';

/**
 * A `dependsOn` edge points from the dependent to the thing it needs, so impact
 * travels the opposite way: when a target goes down, every source joined to it
 * by a *critical* edge goes down too, and so on transitively. `hasPart` edges
 * carry impact the other way — a failed system takes its contents with it.
 */
export interface BlastRadiusLink {
  id: string;
  source: string | { id: string };
  target: string | { id: string };
  relation: string;
  grade?: DependencyGrade;
}

export interface BlastRadius {
  /** Nodes taken down by the failure, excluding the failed nodes themselves. */
  impactedNodes: Set<string>;
  /** Critical edges along which the failure propagated. */
  impactedLinks: Set<string>;
  /** Hops from the nearest failed system, for each impacted node. */
  depthByNode: Map<string, number>;
}

/** d3-force rewrites link endpoints from ids to node objects once simulated. */
const idOf = (endpoint: string | { id: string }): string =>
  typeof endpoint === 'string' ? endpoint : endpoint.id;

/**
 * @param maxDepth - Hops to follow, where 1 is the systems depending directly on
 * a failed one. Defaults to unlimited.
 */
export function computeBlastRadius(
  failed: Set<string>,
  links: BlastRadiusLink[],
  maxDepth: number = Infinity,
): BlastRadius {
  const impactedNodes = new Set<string>();
  const impactedLinks = new Set<string>();
  const depthByNode = new Map<string, number>();
  if (failed.size === 0 || maxDepth < 1) {
    return { impactedNodes, impactedLinks, depthByNode };
  }

  // Impact travels along two kinds of edge, in opposite directions:
  //   dependsOn (critical): target fails → the source that needs it fails
  //   hasPart:              parent fails → everything it contains fails
  const impactEdges = new Map<string, Array<{ next: string; linkId: string }>>();
  const addEdge = (from: string, next: string, linkId: string) => {
    const entry = impactEdges.get(from) ?? [];
    entry.push({ next, linkId });
    impactEdges.set(from, entry);
  };

  for (const link of links) {
    const source = idOf(link.source);
    const target = idOf(link.target);

    if (link.relation === 'hasPart') {
      addEdge(source, target, link.id);
      continue;
    }
    if (link.relation !== 'dependsOn') continue;

    const grade = link.grade ?? { criticality: 'unknown' as const, severity: '' };
    if (dependencyTier(grade) !== 'critical') continue;
    addEdge(target, source, link.id);
  }

  const down = new Set(failed);
  let frontier = [...failed];
  let depth = 0;

  while (frontier.length > 0 && depth < maxDepth) {
    depth++;
    const next: string[] = [];
    for (const current of frontier) {
      for (const edge of impactEdges.get(current) ?? []) {
        impactedLinks.add(edge.linkId);
        if (down.has(edge.next)) continue;
        down.add(edge.next);
        // Only nodes the user did not fail themselves count as collateral.
        if (!failed.has(edge.next)) {
          impactedNodes.add(edge.next);
          depthByNode.set(edge.next, depth);
        }
        next.push(edge.next);
      }
    }
    frontier = next;
  }

  return { impactedNodes, impactedLinks, depthByNode };
}
